import React from "react";
import cn from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { XIcon } from "lucide-react";
import ClockIcon from "../../../../shared/icons/ClockIcon";
import CheckIcon from "../../../../shared/icons/CheckIcon";
import style from "../../../../orders-new/order-info-expeditor/components/tabs/HistoryTab.module.scss";

const STATUS_LABEL_KEYS = {
  created: "statusCreated",
  assigned: "statusAssigned",
  assignedexecutor: "statusAssignedExecutor",
  booked: "statusBooked",
  confirmed: "statusConfirmed",
  processloadingcargo: "statusProcessLoadingCargo",
  onthetrip: "statusOnTheTrip",
  processunloadingcargo: "statusProcessUnloadingCargo",
  completedbyexecutor: "statusCompletedByExecutor",
  completed: "statusCompleted",
  canceled: "statusCancelled",
  cancelled: "statusCancelled",
  awaitingoffer: "statusAwaitingOffer",
  return: "statusReturn",
  inrebid: "statusInRebid",
  rebidclosed: "statusRebidClosed",
};

const PRICE_STATUS_KEYS = {
  awaiting: "priceChangedAwaiting",
  confirmed: "priceChangedConfirmed",
  rejected: "priceChangedRejected",
};

const PRICE_STATUS_ICON_CLASS = {
  awaiting: "icon_awaiting",
  confirmed: "icon_confirmed",
  rejected: "icon_rejected",
};

const PRICE_STATUS_CARD_CLASS = {
  awaiting: "card_awaiting",
  confirmed: "card_confirmed",
  rejected: "card_rejected",
};

const normalizeCode = (value) => String(value || "").toLowerCase();

const getPriceIcon = (status) => {
  if (status === "confirmed") return <CheckIcon width="22" height="22" />;
  if (status === "rejected") return <XIcon size={22} />;
  return <ClockIcon fill="#6D7885" type={2} width={26} height={26} />;
};

const getStatusLabel = (item, t, assignmentMethod) => {
  const statusCode = normalizeCode(item?.statusCode);
  if (assignmentMethod === "Auction") {
    if (statusCode === "created") return t("orderInfoClient.timeline.stepTitles.createdAuction");
    if (statusCode === "awaitingoffer") return t("orderInfoExpeditor.stepper.auction");
  }
  const statusName = String(item?.statusName || "").trim();
  if (statusName) return statusName;

  const labelKey = STATUS_LABEL_KEYS[normalizeCode(item?.statusCode)];
  return labelKey
    ? t(`orderInfoExpeditor.history.${labelKey}`)
    : item?.statusCode || "-";
};

const formatCreatedAt = (createdAt) =>
  createdAt ? moment(createdAt).format("DD.MM.YYYY, HH:mm") : "";

const getDescription = (item) => {
  const parts = [item?.author, item?.moderator, item?.addressName, item?.comment]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  return [...new Set(parts)].join(" • ");
};

const AviationExpeditorHistoryTab = ({ history = [], order, assignmentMethod = order?.assignmentMethod }) => {
  const { t } = useTranslation();

  if (!history.length) {
    return <div className={style.empty}>{t("orderInfoExpeditor.history.empty")}</div>;
  }

  const lastIndex = history.length - 1;

  return (
    <div className={style.wrapper}>
      {history.map((item, index) => {
        const hasNext = index < lastIndex;
        const statusCode = normalizeCode(item?.statusCode);

        if (statusCode === "pricechanged") {
          const priceStatus = normalizeCode(item?.changedPriceStatus);
          const statusKey = PRICE_STATUS_KEYS[priceStatus];

          return (
            <div
              key={`${item?.createdAt || "history"}-${index}`}
              className={style.row}
              data-testid="aviation-expeditor-history-row"
            >
              <div className={cn(style.left, { [style.left_connected]: hasNext })}>
                <div
                  className={cn(
                    style.icon,
                    style[PRICE_STATUS_ICON_CLASS[priceStatus]],
                  )}
                >
                  {getPriceIcon(priceStatus)}
                </div>
              </div>

              <div
                className={cn(
                  style.card,
                  style.card_price,
                  style[PRICE_STATUS_CARD_CLASS[priceStatus]],
                )}
              >
                <div className={style.card_top}>
                  <div className={style.card_title_row}>
                    <span className={style.card_title}>
                      {t("orderInfoExpeditor.history.amountChange")}:{" "}
                      {item?.changedPriceText || "-"}
                    </span>
                    {statusKey ? (
                      <span className={cn(style.badge, style[`badge_${priceStatus}`])}>
                        <span className={style.badge_dot} />
                        {t(`orderInfoExpeditor.history.${statusKey}`)}
                      </span>
                    ) : null}
                  </div>
                  <span className={style.card_date}>
                    {formatCreatedAt(item?.createdAt)}
                  </span>
                </div>
                {item?.comment ? (
                  <p className={style.card_desc}>
                    {t("orderInfoExpeditor.history.priceChangedReason")} {item.comment}
                  </p>
                ) : null}
              </div>
            </div>
          );
        }

        const isCanceled = statusCode === "canceled" || statusCode === "cancelled";
        const isReturnToTender = statusCode === "returntotender";
        const isExpeditorRejected = statusCode === "expeditorrejected";
        const isInRebid = statusCode === "inrebid";
        const isRebidClosed = statusCode === "rebidclosed";
        const isRejected = isCanceled || isReturnToTender || isExpeditorRejected;
        const isActive = index === lastIndex && statusCode !== "completed" && !isRejected && !isRebidClosed;
        const label = isExpeditorRejected
          ? t("orderInfoExpeditor.history.statusRejectedByExpeditor")
          : isCanceled
            ? t("orderInfoExpeditor.history.statusCanceledByPartner")
            : isReturnToTender
              ? t(
                  assignmentMethod === "Auction"
                    ? "orderInfoClient.timeline.stepTitles.returnToAuction"
                    : "orderInfoExpeditor.history.statusReturnToTender",
                )
              : getStatusLabel(item, t, assignmentMethod);
        const description = isExpeditorRejected || isReturnToTender
          ? item.comment && `${t("orderInfoExpeditor.history.priceChangedReason")} ${item.comment}`
          : isInRebid || isRebidClosed
            ? t("orderInfoExpeditor.history.rebidDescription", {
                date: formatCreatedAt(order?.tenderDateEnd),
              })
            : getDescription(item);

        return (
          <div
            key={`${item?.createdAt || "history"}-${index}`}
            className={style.row}
            data-testid="aviation-expeditor-history-row"
          >
            <div className={cn(style.left, { [style.left_connected]: hasNext })}>
              <div
                className={cn(style.icon, {
                  [style.icon_active]: isActive,
                  [style.icon_rejected]: isRejected,
                })}
              >
                {isRejected ? (
                  <XIcon size={22} />
                ) : (
                  <CheckIcon width="22" height="22" />
                )}
              </div>
            </div>

            <div
              className={cn(style.card, {
                [style.card_active]: isActive,
                [style.card_rejected]: isRejected,
              })}
            >
              <div className={style.card_top}>
                <div className={style.card_title_row}>
                  <span className={style.card_title}>{label}</span>
                  {isActive ? (
                    <span className={style.badge}>
                      <span className={style.badge_dot} />
                      {t("orderInfoExpeditor.history.now")}
                    </span>
                  ) : null}
                </div>
                <span className={style.card_date}>
                  {formatCreatedAt(item?.createdAt)}
                </span>
              </div>
              {description ? <p className={style.card_desc}>{description}</p> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AviationExpeditorHistoryTab;
